"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import * as LucideIcons from "lucide-react";
import { MessageCircle } from "lucide-react";
import Loader from "@/components/Loader";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchContactHeroes } from "@/store/slices/landingSlice";

const ContactHeroSection = () => {
  const { mode } = useAppSelector((state) => state.theme);
  const dispatch = useAppDispatch();
  const { data: contactHeroData, loading } = useAppSelector(
    (state) => state.landing.contactHero,
  );

  const heroData =
    contactHeroData && contactHeroData.length > 0 ? contactHeroData[0] : null;

  useEffect(() => {
    dispatch(fetchContactHeroes());
  }, [dispatch]);

  if (loading) {
    return (
      <section
        className={`relative flex justify-center items-center pt-20 pb-10 min-h-[70vh] transition-colors duration-700 ${
          mode === "dark" ? "bg-[#0B0F19]" : "bg-white"
        }`}
      >
        <Loader
          loading={loading}
          loadingStates={[
            { text: "Loading contact hero" },
            { text: "Preparing contact channels" },
            { text: "Almost ready" },
          ]}
          mode={mode}
          className="min-h-[220px]"
        />
      </section>
    );
  }

  // Use default values if no data
  const rawBadge = heroData?.badgeText || "Get In Touch";
  const badgeText = rawBadge
    .replace(/[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F1E0}-\u{1F1FF}]/gu, "")
    .trim() || "Get In Touch";
  const mainTitle = heroData?.mainTitle || "Let's Start a Conversation";
  const subtitle =
    heroData?.subtitle ||
    "Have a project in mind? Questions about our services? We'd love to hear from you. Our team is ready to help transform your ideas into reality.";
  const contacts = heroData?.contacts || [];

  return (
    <section
      className={`relative flex justify-center items-center pt-20 pb-10 min-h-[70vh] overflow-hidden transition-colors duration-700 ${
        mode === "dark"
          ? "bg-[#0B0F19]"
          : "bg-gradient-to-br from-slate-50 via-purple-50 to-blue-50"
      }`}
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        {mode === "dark" ? (
          <>
            <motion.div
              className="top-20 left-10 absolute bg-[#8B31FF] opacity-20 blur-[120px] rounded-full w-72 h-72 mix-blend-screen filter"
              animate={{
                x: [0, 100, 0],
                y: [0, 50, 0],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <motion.div
              className="right-10 bottom-20 absolute bg-[#8B31FF] opacity-20 blur-[120px] rounded-full w-96 h-96 mix-blend-screen filter"
              animate={{
                x: [0, -100, 0],
                y: [0, 100, 0],
              }}
              transition={{
                duration: 25,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </>
        ) : (
          <>
            <motion.div
              className="top-20 left-10 absolute bg-[#8B31FF]/10 blur-[100px] rounded-full w-72 h-72 filter"
              animate={{
                x: [0, 100, 0],
                y: [0, 50, 0],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <motion.div
              className="right-10 bottom-20 absolute bg-[#8B31FF]/10 blur-[100px] rounded-full w-96 h-96 filter"
              animate={{
                x: [0, -100, 0],
                y: [0, 100, 0],
              }}
              transition={{
                duration: 25,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </>
        )}
      </div>

      {/* Grid Pattern */}
      <div
        className={`absolute inset-0 transition-opacity duration-700 ${mode === "dark" ? "opacity-[0.05]" : "opacity-[0.03]"}`}
        style={{
          backgroundImage:
            mode === "dark"
              ? `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`
              : `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />

      <div className="z-10 relative mx-auto px-4 sm:px-6 lg:px-8 py-20 max-w-7xl text-center">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div
            className={`inline-flex items-center gap-2.5 px-5 py-2 rounded-full border backdrop-blur-md transition-all duration-300 ${
              mode === "dark"
                ? "bg-[#8B31FF]/15 border-[#8B31FF]/30 text-white shadow-[0_0_20px_rgba(139,49,255,0.2)]"
                : "bg-white/90 border-[#8B31FF]/25 text-[#8B31FF] shadow-sm shadow-[#8B31FF]/10"
            }`}
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
            </span>
            <span className="font-bold text-xs sm:text-sm tracking-wider uppercase">
              {badgeText}
            </span>
          </div>
        </motion.div>

        {/* Main Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 sm:mb-6 tracking-tight leading-[1.12] ${
            mode === "dark" ? "text-white" : "text-gray-900"
          }`}
        >
          {mainTitle.split(" ").slice(0, -1).join(" ")}
          <br />
          <span className="bg-clip-text bg-gradient-to-r from-[#8B31FF] to-[#8B31FF] text-transparent">
            {mainTitle.split(" ").slice(-1)[0]}
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className={`mx-auto mb-12 max-w-3xl text-lg sm:text-xl md:text-2xl leading-relaxed transition-colors duration-500 ${
            mode === "dark" ? "text-gray-400" : "text-gray-600"
          }`}
        >
          {subtitle}
        </motion.p>

        {/* Quick Contact Cards */}
        {contacts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.7 }}
            className={`gap-6 grid grid-cols-1 sm:grid-cols-${Math.min(contacts.length, 3)} mx-auto max-w-4xl`}
          >
            {contacts.map((item, index) => {
              // @ts-ignore
              const Icon = LucideIcons[item.icon] || LucideIcons.Mail;

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.9 + index * 0.1, duration: 0.6 }}
                  whileHover={{ y: -8, scale: 1.03 }}
                  className={`group backdrop-blur-md p-6 border rounded-lg text-center cursor-pointer transition-all duration-300 ${
                    mode === "dark"
                      ? "bg-white/5 border-white/10 hover:border-[#8B31FF]/50 hover:bg-white/10 shadow-lg hover:shadow-[#8B31FF]/15"
                      : "bg-white border-gray-100 hover:border-[#8B31FF]/30 shadow-sm hover:shadow-xl hover:shadow-purple-500/10"
                  }`}
                >
                  <motion.div
                    whileHover={{ rotate: 360, scale: 1.15 }}
                    transition={{ duration: 0.6 }}
                    className="inline-flex p-4 rounded-lg bg-gradient-to-br from-[#8B31FF] to-[#6D28D9] mb-4 shadow-lg shadow-purple-500/25"
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </motion.div>
                  <h3
                    className={`mb-2 font-semibold text-sm transition-colors duration-500 ${
                      mode === "dark" ? "text-purple-200" : "text-[#8B31FF]"
                    }`}
                  >
                    {item.title}
                  </h3>
                  <p
                    className={`font-medium transition-colors duration-500 ${mode === "dark" ? "text-white" : "text-gray-900"}`}
                  >
                    {item.value}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        )}

      </div>
    </section>
  );
};

export default ContactHeroSection;
